import React from 'react'
import { ListItem, ListItemButton, ListItemIcon, ListItemText, Theme, useTheme } from '@mui/material'
import { sidebarSelectedTint } from '../../theme'

export interface MenuItem {
    name: string
    // The outlined glyph, shown while this item isn't the active destination.
    icon: React.ReactNode
    // The filled glyph, shown once this item is the active destination. Falls back to `icon`
    // for the handful of items (actions, not destinations) that never carry a selected state.
    iconActive?: React.ReactNode
    click_navigate: () => string
    route: string
}

// Sized for the tallest label (some locales wrap to 2 lines) so every collapsed row shares
// one height instead of looking uneven; a 1-line label just centers in the extra space.
const collapsedRowMinHeight = 64

const listItemButtonStyles = (labelsExpanded: boolean, theme: Theme) => ({
    minHeight: labelsExpanded ? 48 : collapsedRowMinHeight,
    flexDirection: labelsExpanded ? 'row' : 'column',
    alignItems: 'center',
    // Column flex defaults to packing content at the top, which left more empty space below
    // the label than above the icon once minHeight exceeded the content's natural height.
    justifyContent: labelsExpanded ? 'flex-start' : 'center',
    gap: labelsExpanded ? 0 : 0.125,
    // Trimmed from MUI's default 8px top/bottom — tightens the space between consecutive
    // rows without touching the row's own content layout.
    ...(!labelsExpanded && { pt: 0.5, pb: 0.5 }),
    '&.Mui-selected': {
        backgroundColor: sidebarSelectedTint(theme),
        '&:hover': {
            backgroundColor: sidebarSelectedTint(theme),
        },
    },
})

const listItemIconStyles = (labelsExpanded: boolean, isActive: boolean) => ({
    minWidth: 0,
    mr: labelsExpanded ? 3 : 'auto',
    justifyContent: 'center',
    ...(isActive && { color: 'primary.main' }),
})

const listItemTextStyles = (labelsExpanded: boolean, animateLabel: boolean, isActive: boolean, theme: Theme) => ({
    textAlign: labelsExpanded ? 'left' : 'center',
    // Matches the icon's selected color — one accent applied consistently to both.
    ...(isActive && { color: 'primary.main' }),
    // Reserves space for 2 lines so a 1-line label centers at the same height a 2-line
    // label would use, instead of sitting higher. 20.4px = 2 × (8.5px font × 1.2 line-height).
    ...(!labelsExpanded && {
        // Overrides MUI's default 4px margin, which threw off that centering (it's not
        // part of the button's own gap, so it added untracked extra space after the text).
        margin: 0,
        minHeight: '20.4px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    }),
    '& .MuiTypography-root': labelsExpanded
        ? {}
        : {
              // Regular weight: at this size anything lighter renders thin and blurry
              fontWeight: 400,
              fontSize: '8.5px',
              lineHeight: 1.2,
              ...(animateLabel && {
                  // Hold the tiny label invisible until the drawer's width transition
                  // finishes, otherwise it pops in while the drawer is still wide.
                  animation: `sidebar-label-fade-in 150ms ease ${theme.transitions.duration.enteringScreen}ms backwards`,
                  '@keyframes sidebar-label-fade-in': {
                      from: { opacity: 0 },
                      to: { opacity: 1 },
                  },
                  '@media (prefers-reduced-motion: reduce)': {
                      animation: 'none',
                  },
              }),
          },
})

interface MenuItemProps {
    item: MenuItem
    // Not the drawer's own open/collapsed state — this lags behind it, flipping once the
    // drawer's width transition is far enough along that full-width labels are actually
    // safe to show. See the ResizeObserver effect in Sidebar.tsx that derives it.
    labelsExpanded: boolean
    animateLabel: boolean
    isActive: boolean
    onClick: () => void
    disabled?: boolean
}

export const SidebarMenuItem: React.FC<MenuItemProps> = ({
    item,
    labelsExpanded,
    animateLabel,
    isActive,
    onClick,
    disabled,
}) => {
    const theme = useTheme()
    return (
        <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
                selected={isActive}
                onClick={onClick}
                disabled={disabled}
                sx={listItemButtonStyles(labelsExpanded, theme)}
            >
                <ListItemIcon sx={listItemIconStyles(labelsExpanded, isActive)}>
                    {isActive ? (item.iconActive ?? item.icon) : item.icon}
                </ListItemIcon>
                <ListItemText
                    primary={item.name}
                    sx={listItemTextStyles(labelsExpanded, animateLabel, isActive, theme)}
                />
            </ListItemButton>
        </ListItem>
    )
}
